/**
 * Per-app memory of which DJ-app playlists the user UNCHECKED in the
 * "Sync from <App>" picker. One-click imports (Re-sync, change-banner "Sync
 * now", watcher card) send these as excludeKeys so a crate the user skipped
 * doesn't flood back in — while brand-new crates (in neither list) still
 * arrive, matching the legacy import-everything behavior for users who never
 * deselect anything.
 *
 * Keys come from djapp:previewImport (app id when the app has one, folder
 * path otherwise — see importedPlaylistReconciler.playlistSelectionKey).
 */
// Keyed per signed-in user (accounts are isolated on shared machines —
// migration 012); userId null = the signed-out bucket.
const storageKey = (app, userId) => `${app}.importExcludedKeys${userId ? `:${userId}` : ''}`;

export function getImportExclusions(app, userId = null) {
    try {
        const v = JSON.parse(localStorage.getItem(storageKey(app, userId)));
        return Array.isArray(v) ? v.filter(k => typeof k === 'string') : [];
    } catch {
        return [];
    }
}

export function setImportExclusions(app, keys, userId = null) {
    try {
        localStorage.setItem(storageKey(app, userId), JSON.stringify(keys || []));
    } catch { /* private mode etc. — worst case the next re-sync imports all */ }
}

/**
 * Whether THIS user has ever applied the picker for this app (every apply
 * stores its exclusions, empty list included). Distinguishes "no exclusions
 * because none were chosen" from "never made a selection at all" — the
 * getImportExclusions() return value ([]) can't. Used to tell a real
 * stop-syncing consequence from a first-run non-opt-in.
 */
export function hasImportSelection(app, userId = null) {
    try {
        return localStorage.getItem(storageKey(app, userId)) !== null;
    } catch {
        return false;
    }
}

/** The picker's pseudo-key for "Tracks not in any playlist" (mirrors the backend's looseTracksKey). */
export const looseTracksKey = (app) => `${app}:__loose__`;

/**
 * Selection key of a LOCAL playlist row (from db.playlists.getAll), mirroring
 * the backend's playlistSelectionKey for parsed DJ-app playlists — so the
 * sidebar can tell which of its imported playlists the user excluded from
 * sync. Null when the row's primary source columns belong to another app
 * (multi-app links keep the first app's columns); those rows just go untagged.
 */
export function localPlaylistSelectionKey(app, playlist) {
    const pfx = `${app}:`;
    if (playlist?.source_id && playlist.source_id.startsWith(pfx)) return `${app}:id:${playlist.source_id.slice(pfx.length)}`;
    if (playlist?.source_path && playlist.source_path.startsWith(pfx)) return `${app}:path:${playlist.source_path.slice(pfx.length)}`;
    return null;
}
